import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { UploadCloud, FileArchive, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, ACCEPTED_EXTENSIONS, useUploadXml } from '../GestaoDocumentosProvider';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cnpj: string;
}

export function UploadXmlDialog({ open, onOpenChange, cnpj }: Props) {
  const {
    file,
    status,
    errorMessage,
    dragOver,
    setDragOver,
    inputRef,
    reset,
    handleClose,
    handleFileChange,
    handleDrop,
    handleUpload,
    isUploading,
  } = useUploadXml(cnpj);

  function onOpenChangeMerged(nextOpen: boolean) {
    handleClose(nextOpen);
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChangeMerged}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload de XML</DialogTitle>
        </DialogHeader>

        {status === 'success' ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="font-semibold text-foreground">Arquivo enviado com sucesso!</p>
            <p className="text-sm text-muted-foreground">
              O arquivo será processado em breve e os documentos aparecerão na listagem.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Dropzone */}
            <div
              role="button"
              tabIndex={0}
              aria-label="Área de upload — clique ou arraste um arquivo"
              className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer',
                dragOver
                  ? 'border-accent bg-accent/10'
                  : 'border-muted-foreground/25 hover:border-accent hover:bg-accent/5'
              )}
              onClick={() => inputRef.current?.click()}
              onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
              onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <UploadCloud className="h-10 w-10 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">
                  Clique para selecionar ou arraste aqui
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Somente arquivos{' '}
                  {ACCEPTED_EXTENSIONS.map((ext, i) => (
                    <span key={ext}>
                      <span className="font-semibold">{ext}</span>
                      {i < ACCEPTED_EXTENSIONS.length - 1 ? ' ou ' : ''}
                    </span>
                  ))}
                </p>
              </div>
            </div>

            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              className="hidden"
              onChange={handleFileChange}
            />

            {file && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2">
                <FileArchive className="h-5 w-5 shrink-0 text-accent" />
                <span className="flex-1 truncate text-sm text-foreground">{file.name}</span>
                <button
                  type="button"
                  aria-label="Remover arquivo"
                  onClick={reset}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {isUploading && (
              <div className="space-y-1.5">
                <Progress value={undefined} className="h-1.5 animate-pulse" />
                <p className="text-xs text-muted-foreground text-center">Enviando arquivo...</p>
              </div>
            )}

            {(status === 'error' || (status === 'idle' && errorMessage)) && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {status === 'success' ? (
            <Button onClick={() => onOpenChangeMerged(false)}>Fechar</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChangeMerged(false)}
                disabled={isUploading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!file || isUploading}
                className="bg-[#aed561] text-white hover:bg-[#8ab83a]"
              >
                {isUploading ? 'Enviando...' : 'Enviar'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
